import React from 'react';
import DynamicTable from '../widgets/dynamicTable/DynamicTable.jsx';
import PropTypes from 'prop-types';
import HelperMetiers360 from '../../services/HelpersMetiers360.js';
import AuthorizationChecker from '../../services/AuthorizationChecker.js';
import { useToast } from '../../hooks/useToast.jsx';
import { useModal } from '../../hooks/useModal.jsx';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { Alert, Button } from 'react-bootstrap';
import LogsComponent from "../logs/LogsComponent.jsx";
import CategoryFormComponent, { submitCategoryForm } from './CategoryFormComponent.jsx';

/**
 * 
 * @param {Array} categoriesToDisplay array of all client categories to be rendered
 
 * @example
 *  <CategoriesTableComponent categoriesToDisplay={categories} />
 * 
 */

const CategoriesTableComponent = ({ categoriesToDisplay = [] }) => {
    const { allCategories } = useStoreState(state => state.categories);
    const { updateCategory, deleteCategory } = useStoreActions(actions => actions.categories);
    const toast = useToast();
    const { modalComponent, setModalShow, setModalData, modalData, setIsSending } = useModal();

    const openEditModal = (category) => {
        const handleSubmit = () => {
            setIsSending(true);
            submitCategoryForm({ category, allCategories, action: updateCategory })
                .then(() => {
                    toast.success();
                    setModalShow(false);
                })
                .finally(() => setIsSending(false));
        }

        setModalData({
            ...modalData,
            header: <>Edition de la catégorie client</>,
            content: <CategoryFormComponent category={category} />,
            cancelButton: 'Annuler',
            onValidate: handleSubmit,
            size: 'xl',
        });
        setModalShow(true);
    }

    const openDeleteModal = (category) => {
        setModalData({
            ...modalData,
            header: <>Suppression de la catégorie client</>,
            content: <>
                <h5>
                    Êtes vous sûr⋅e de vouloir supprimer la catégorie client<strong> {category.name}</strong> &nbsp;?
                </h5>
                {category.countClients > 0 &&
                    <Alert variant='danger'>
                        <strong>Attention :</strong> La catégorie sera retirée de <strong>{category.countClients}</strong> clients !
                    </Alert>}
            </>,
            resetButton: 'Supprimer',
            cancelButton: 'Annuler',
            onReset: () => {
                deleteCategory({ uniqueId: category.uniqueId })
                    .then(() => {
                        toast.success();
                        setModalShow(false);
                    })
                    .finally(() => setIsSending(false));
            },
        });
        setModalShow(true);
    }

    const sortType = [
        {
            value: 'name', label: 'Nom',
            test: HelperMetiers360.isArrayContainsValue(categoriesToDisplay, "name"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.name, b.name),
            display: (category) => category.name
        },
        {
            value: 'countClients', label: 'Occurrence',
            test: HelperMetiers360.isArrayContainsValue(categoriesToDisplay, "countClients"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.countClients, b.countClients),
            display: (category) => category.countClients
        },
        {
            value: 'edit', label: 'Éditer',
            test: AuthorizationChecker.hasUpdateRights('categories'),
            display: (category) => <Button variant="secondary" size="sm" onClick={() => { openEditModal(category) }}>
                <i className="fas fa-edit"></i>
            </Button>
        },
        {
            value: 'delete', label: 'Supprimer',
            test: AuthorizationChecker.hasDeleteRights('categories'),
            display: (category) => <Button variant="danger" size="sm" onClick={() => { openDeleteModal(category) }}>
                <i className="fas fa-trash-alt"></i>
            </Button>
        },
        {
            value: 'actionLogs', label: "Logs",
            test: AuthorizationChecker.isAdmin(),
            display: (category) => <LogsComponent entityNameFromComponent="categories" uniqueIdFromComponent={category.uniqueId} />,
            flatDisplay: () => null
        }
    ];

    return <>
        <DynamicTable
            contentTable={categoriesToDisplay}
            contentSort={sortType}
            valueInitSort="name"
            index='uniqueId'
        />
        {modalComponent}
    </>
}

CategoriesTableComponent.propTypes = {
    categoriesToDisplay: PropTypes.array.isRequired
};
export default CategoriesTableComponent;